import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, useBusinessUnit } from '@/App';
import { commissions as commissionsApi, agents as agentsApi } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Plus, Search, Pencil, Trash2, TrendingUp, 
  Building2, Calendar, DollarSign, Upload, User, X, CheckSquare, Eye, ChevronsUpDown, Check
} from 'lucide-react';
import axios from 'axios';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ExportDropdown from '@/components/ExportDropdown';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

function CarrierCombobox({ carriers, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = carriers.filter(c => c && c.toLowerCase().includes(search.toLowerCase()));
  const isNew = search && !carriers.some(c => c.toLowerCase() === search.toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal" data-testid="carrier-combobox">
          {value || <span className="text-muted-foreground">Select or type carrier...</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            placeholder="Search or type new carrier..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="carrier-search-input"
          />
          {(search || value) && (
            <button onClick={() => { setSearch(''); onChange(''); }} className="text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="max-h-[200px] overflow-y-auto p-1">
          {filtered.map(carrier => (
            <button
              key={carrier}
              className={cn(
                "relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-slate-100",
                value === carrier && "bg-slate-100"
              )}
              onClick={() => { onChange(carrier); setOpen(false); setSearch(''); }}
            >
              <Check className={cn("mr-2 h-4 w-4", value === carrier ? "opacity-100" : "opacity-0")} />
              {carrier}
            </button>
          ))}
          {isNew && (
            <button
              className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-blue-50 text-blue-600 font-medium"
              onClick={() => { onChange(search); setOpen(false); setSearch(''); }}
              data-testid="carrier-add-new"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add "{search}" as new carrier
            </button>
          )}
          {filtered.length === 0 && !isNew && (
            <p className="py-4 text-center text-sm text-muted-foreground">No carriers found.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function MGACommissions() {
  const { user } = useAuth();
  const { businessUnit } = useBusinessUnit();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAgent = user?.role === 'agent';
  const fileInputRef = useRef(null);
  const highlightedRowRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  // Get highlightId and agent filter from URL if present
  const highlightId = searchParams.get('highlightId');
  const [highlightedCommissionId, setHighlightedCommissionId] = useState(highlightId);
  const agentFilterFromUrl = searchParams.get('agent');

  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [hiddenCarriers, setHiddenCarriers] = useState([]);
  
  // Filters - initialize filterSearch from URL if present
  const [filterAgent, setFilterAgent] = useState('_all_');
  const [filterCarrier, setFilterCarrier] = useState('_all_');
  const [filterTransType, setFilterTransType] = useState('_all_');
  const [filterSearch, setFilterSearch] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('agent') || '';
  });
  const [filterYear, setFilterYear] = useState('_all_');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  
  // Selection state for mass delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Year grouping - track expanded years (default: all collapsed)
  const [expandedYears, setExpandedYears] = useState({});
  
  // Carrier manager modal
  const [showCarrierManager, setShowCarrierManager] = useState(false);

  // Form data for new columns
  const emptyForm = {
    carrier: '',
    agent_id: '',
    agent_code: '',
    category: 'INSURANCE',
    insured_name: '',
    contract_no: '',
    coverage_name: '',
    transaction_date: '',
    transaction_type: 'RENEW',
    basis_amount: '',
    rpl_pct: '0',
    share_pct: '0',
    comm_pct: '',
    agent_split_pct: '',
    paid_to_agent_date: '',
    memo: '',
    accounting_account_id: '',
    is_brokerage_direct: false
  };
  const [formData, setFormData] = useState(emptyForm);

  const [chartOfAccounts, setChartOfAccounts] = useState([]);

  // Import state
  const [importData, setImportData] = useState(null);
  const [importMappings, setImportMappings] = useState({});
  const [importLoading, setImportLoading] = useState(false);

  // Load data - fetch all, filter client-side for year
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      // Don't filter by year on server - do it client-side for proper date handling
      if (filterAgent && filterAgent !== '_all_') params.agent_id = filterAgent;
      if (filterCarrier && filterCarrier !== '_all_') params.carrier = filterCarrier;
      if (filterTransType && filterTransType !== '_all_') params.transaction_type = filterTransType;

      const [commsRes, agentsRes, carriersRes] = await Promise.all([
        commissionsApi.mga.list(params),
        agentsApi.insurance.list(),
        commissionsApi.mga.carriers()
      ]);

      setCommissions(commsRes?.data || []);
      setAgents(agentsRes?.data || []);
      setCarriers(carriersRes?.data?.carriers || []);
    } catch (error) {
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  }, [filterAgent, filterCarrier, filterTransType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load chart of accounts
  useEffect(() => {
    const loadChartOfAccounts = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/accounting/chart-of-accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChartOfAccounts(res.data || []);
      } catch (err) {
        // Silently fail - chart of accounts is optional for viewing commissions
      }
    };
    loadChartOfAccounts();
  }, []);

  // Handle highlightId from URL - scroll to and highlight the commission row
  useEffect(() => {
    if (highlightId && commissions.length > 0 && !loading) {
      // Find the commission and its year
      const commission = commissions.find(c => c.id === highlightId);
      if (commission) {
        const year = getYearFromDate(commission.transaction_date) || getYearFromDate(commission.paid_to_agent_date) || 'Unknown';
        
        // Expand the year containing this commission
        setExpandedYears(prev => ({
          ...prev,
          [year]: true
        }));
        
        // Wait for the DOM to update and then scroll to the row
        setTimeout(() => {
          const rowElement = document.querySelector(`[data-commission-id="${highlightId}"]`);
          if (rowElement) {
            rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Clear the highlightId from URL after a delay
            setTimeout(() => {
              setSearchParams({}, { replace: true });
              setHighlightedCommissionId(null);
            }, 3000); // Remove highlight after 3 seconds
          }
        }, 300);
      }
    }
  }, [highlightId, commissions, loading, setSearchParams]);

  // Helper to parse date string to Date object
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    // Handle MM/DD/YYYY format
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(parts[2], parts[0] - 1, parts[1]);
      }
    }
    // Handle YYYY-MM-DD format
    if (dateStr.includes('-')) {
      return new Date(dateStr);
    }
    return null;
  };

  // Get year from date string
  const getYearFromDate = (dateStr) => {
    if (!dateStr) return null;
    // Handle MM/DD/YYYY format
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) return parts[2];
    }
    // Handle YYYY-MM-DD format
    if (dateStr.includes('-')) {
      return dateStr.substring(0, 4);
    }
    return null;
  };

  // Filter and sort commissions by search and date (latest first)
  const filteredCommissions = useMemo(() => {
    let filtered = commissions;
    
    // Apply year filter
    if (filterYear && filterYear !== '_all_') {
      filtered = filtered.filter(c => {
        const txnYear = getYearFromDate(c.transaction_date);
        const createdYear = c.created_at ? c.created_at.substring(0, 4) : null;
        return txnYear === filterYear || (!txnYear && createdYear === filterYear);
      });
    }
    
    // Apply search filter - search all data fields including amounts
    if (filterSearch) {
      const search = filterSearch.toLowerCase().trim();
      filtered = filtered.filter(c => 
        // Text fields
        (c.insured_name && c.insured_name.toLowerCase().includes(search)) ||
        (c.contract_no && c.contract_no.toLowerCase().includes(search)) ||
        (c.carrier && c.carrier.toLowerCase().includes(search)) ||
        (c.agent_name && c.agent_name.toLowerCase().includes(search)) ||
        (c.coverage_name && c.coverage_name.toLowerCase().includes(search)) ||
        (c.transaction_type && c.transaction_type.toLowerCase().includes(search)) ||
        (c.category && c.category.toLowerCase().includes(search)) ||
        (c.memo && c.memo.toLowerCase().includes(search)) ||
        (c.agent_code && c.agent_code.toLowerCase().includes(search)) ||
        // Date fields
        (c.transaction_date && c.transaction_date.includes(search)) ||
        (c.paid_to_agent_date && c.paid_to_agent_date.includes(search)) ||
        // Numeric fields - convert to string for searching
        (c.amount_due != null && c.amount_due.toString().includes(search)) ||
        (c.agent_comm != null && c.agent_comm.toString().includes(search)) ||
        (c.basis_amount != null && c.basis_amount.toString().includes(search)) ||
        (c.carrier_paid != null && c.carrier_paid.toString().includes(search)) ||
        (c.comm_pct != null && c.comm_pct.toString().includes(search)) ||
        (c.agent_split_pct != null && c.agent_split_pct.toString().includes(search))
      );
    }
    
    // Sort by transaction_date descending (latest first)
    return [...filtered].sort((a, b) => {
      const dateA = parseDate(a.transaction_date) || new Date(a.created_at) || new Date(0);
      const dateB = parseDate(b.transaction_date) || new Date(b.created_at) || new Date(0);
      return dateB - dateA;
    });
  }, [commissions, filterSearch, filterYear]);

  // Calculate summary
  const summary = useMemo(() => {
    const totals = {
      count: filteredCommissions.length,
      amount_due: 0,
      agent_comm: 0,
      net_to_brokerage: 0,
      by_type: {}
    };

    filteredCommissions.forEach(c => {
      totals.amount_due += c.amount_due || 0;
      totals.agent_comm += c.agent_comm || 0;
      totals.net_to_brokerage += c.net_comm_to_brokerage || 0;

      const type = c.transaction_type || 'OTHER';
      if (!totals.by_type[type]) {
        totals.by_type[type] = { count: 0, amount_due: 0 };
      }
      totals.by_type[type].count++;
      totals.by_type[type].amount_due += c.amount_due || 0;
    });

    return totals;
  }, [filteredCommissions]);

  // Mark a commission as viewed (remove highlight)
  const markAsViewed = async (commissionId) => {
    try {
      const res = await fetch(`${API}/api/insurance/commissions/mga/${commissionId}/mark-viewed`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        // Update local state to remove the is_new flag
        setCommissions(prev => prev.map(c => 
          c.id === commissionId ? { ...c, is_new: false } : c
        ));
      }
    } catch (err) {
      // Silently fail - marking as viewed is non-critical
    }
  };

  // Mark all commissions as viewed
  const markAllAsViewed = async () => {
    try {
      const res = await fetch(`${API}/api/insurance/commissions/mga/mark-all-viewed`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        // Update local state to remove all is_new flags
        setCommissions(prev => prev.map(c => ({ ...c, is_new: false })));
        toast.success('All commissions marked as viewed');
      }
    } catch (err) {
      toast.error('Failed to mark commissions as viewed');
    }
  };

  // Fetch carriers from API
  const fetchCarriers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/insurance/commissions/carriers`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCarriers(data.carriers || []);
        setHiddenCarriers(data.hidden || []);
      }
    } catch (err) {
      // Silently fail - carrier list is secondary
    }
  }, []);

  // Delete carrier
  const handleDeleteCarrier = async (carrierName) => {
    if (!window.confirm(`Remove "${carrierName}" from the carrier list?`)) return;
    
    try {
      const res = await fetch(`${API}/api/insurance/commissions/carriers/${encodeURIComponent(carrierName)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        toast.success(`Carrier "${carrierName}" removed`);
        fetchCarriers();
        // Clear the carrier field if it was the deleted one
        if (formData.carrier === carrierName) {
          setFormData({ ...formData, carrier: '' });
        }
      }
    } catch (err) {
      toast.error('Failed to remove carrier');
    }
  };

  // Fetch carriers on mount and when commissions change
  useEffect(() => {
    fetchCarriers();
  }, [fetchCarriers, commissions]);

  // Group commissions by year for collapsible display
  const groupedByYear = useMemo(() => {
    const groups = {};
    
    filteredCommissions.forEach(c => {
      const year = getYearFromDate(c.transaction_date) || getYearFromDate(c.paid_to_agent_date) || 'Unknown';
      if (!groups[year]) {
        groups[year] = {
          commissions: [],
          totals: { count: 0, amount_due: 0, agent_comm: 0, net_to_brokerage: 0 }
        };
      }
      groups[year].commissions.push(c);
      groups[year].totals.count++;
      groups[year].totals.amount_due += c.amount_due || 0;
      groups[year].totals.agent_comm += c.agent_comm || 0;
      groups[year].totals.net_to_brokerage += c.net_comm_to_brokerage || 0;
    });
    
    // Sort years descending (most recent first)
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filteredCommissions]);

  // Toggle year expansion
  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  // Expand/Collapse all years
  const expandAllYears = () => {
    const allExpanded = {};
    groupedByYear.forEach(([year]) => {
      allExpanded[year] = true;
    });
    setExpandedYears(allExpanded);
  };

  const collapseAllYears = () => {
    setExpandedYears({});
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.accounting_account_id) {
      toast.error('Chart of Account is required');
      return;
    }
    try {
      const data = {
        ...formData,
        basis_amount: parseFloat(formData.basis_amount) || 0,
        rpl_pct: parseFloat(formData.rpl_pct) || 0,
        share_pct: parseFloat(formData.share_pct) || 0,
        comm_pct: parseFloat(formData.comm_pct) || 0,
        agent_split_pct: formData.is_brokerage_direct ? 0 : (formData.agent_split_pct ? parseFloat(formData.agent_split_pct) : null),
        agent_id: formData.is_brokerage_direct ? '' : formData.agent_id,
        accounting_account_id: formData.accounting_account_id || null,
        is_brokerage_direct: formData.is_brokerage_direct
      };

      if (editingCommission) {
        await commissionsApi.mga.update(editingCommission.id, data);
        toast.success('Commission updated');
      } else {
        await commissionsApi.mga.create(data);
        toast.success('Commission created');
      }
      
      setShowAddModal(false);
      setEditingCommission(null);
      setFormData(emptyForm);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save commission');
    }
  };

  // Handle edit
  const handleEdit = (commission) => {
    setEditingCommission(commission);
    setFormData({
      carrier: commission.carrier || '',
      agent_id: commission.agent_id || '',
      agent_code: commission.agent_code || '',
      category: commission.category || 'INSURANCE',
      insured_name: commission.insured_name || '',
      contract_no: commission.contract_no || '',
      coverage_name: commission.coverage_name || '',
      transaction_date: commission.transaction_date || '',
      transaction_type: commission.transaction_type || 'RENEW',
      basis_amount: commission.basis_amount?.toString() || '',
      rpl_pct: commission.rpl_pct?.toString() || '0',
      share_pct: commission.share_pct?.toString() || '0',
      comm_pct: commission.comm_pct?.toString() || '',
      agent_split_pct: commission.agent_split_pct?.toString() || '',
      paid_to_agent_date: commission.paid_to_agent_date || '',
      memo: commission.memo || '',
      accounting_account_id: commission.accounting_account_id || '',
      is_brokerage_direct: commission.is_brokerage_direct || false
    });
    setShowAddModal(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await commissionsApi.mga.delete(deleteConfirm.id);
      toast.success('Commission deleted');
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete commission');
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      // Delete each selected commission
      const deletePromises = Array.from(selectedIds).map(id => 
        commissionsApi.mga.delete(id)
      );
      await Promise.all(deletePromises);
      
      toast.success(`Deleted ${selectedIds.size} commission(s)`);
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      loadData();
    } catch (error) {
      toast.error('Failed to delete some commissions');
    }
  };

  // Toggle selection for a single row
  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Toggle select all (filtered commissions)
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredCommissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCommissions.map(c => c.id)));
    }
  };

  // Handle file upload for import
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setImportLoading(true);
    try {
      const response = await commissionsApi.mga.importPreview(formData);
      const preview = response?.data || response;
      setImportData(preview);
      setImportMappings(preview.auto_mappings || {});
      setShowImportModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to read file');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Execute import
  const handleImportExecute = async () => {
    if (!importData?.session_id) return;

    setImportLoading(true);
    try {
      const response = await commissionsApi.mga.importExecute({
        session_id: importData.session_id,
        mappings: importMappings
      });
      const result = response?.data || response;
      toast.success(`Imported ${result.imported} commissions`);
      if (result.created_agents?.length > 0) {
        toast.info(`Created ${result.created_agents.length} new agents`);
      }
      setShowImportModal(false);
      setImportData(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  // Transaction type badge color
  const getTypeBadge = (type) => {
    const colors = {
      FYO: 'bg-blue-100 text-blue-800',
      FYC: 'bg-green-100 text-green-800',
      RENEW: 'bg-purple-100 text-purple-800',
      TRANSFER: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-slate-100 text-slate-800';
  };

  // Export column definitions for insurance commissions
  const insuranceExportColumns = [
    { key: 'carrier', label: 'Carrier' },
    { key: 'agent_name', label: 'Agent' },
    { key: 'agent_code', label: 'Agent Code' },
    { key: 'category', label: 'Category' },
    { key: 'insured_name', label: 'Insured Name' },
    { key: 'contract_no', label: 'Contract No' },
    { key: 'coverage_name', label: 'Coverage' },
    { key: 'transaction_date', label: 'Date' },
    { key: 'transaction_type', label: 'Type' },
    { key: 'basis_amount', label: 'Basis Amount', format: 'currency' },
    { key: 'comm_pct', label: 'Comm %' },
    { key: 'amount_due', label: 'Amount Due', format: 'currency' },
    { key: 'agent_split_pct', label: 'Agent Split %' },
    { key: 'agent_comm', label: 'Agent Comm', format: 'currency' },
    { key: 'paid_to_agent_date', label: 'Paid to Agent' },
    { key: 'net_comm_to_brokerage', label: 'Net to Brokerage', format: 'currency' },
    { key: 'memo', label: 'Memo' },
  ];

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="mga-commissions">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Insurance Commissions</h2>
          <p className="text-sm text-slate-500">Manage insurance commission transactions</p>
        </div>
        {!isAgent && (
          <div className="flex flex-wrap gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".xlsx,.xls,.xlsm,.pdf"
              className="hidden"
            />
            <ExportDropdown
              filteredData={filteredCommissions}
              allData={commissions}
              columns={insuranceExportColumns}
              filenamePrefix="insurance_commissions"
              title="Insurance Commissions"
            />
            <Button variant="outline" size="sm" className="h-9 touch-manipulation" onClick={() => fileInputRef.current?.click()} disabled={importLoading}>
              <Upload className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Import</span>
            </Button>
            <Button size="sm" className="h-9 touch-manipulation" onClick={() => { setEditingCommission(null); setFormData(emptyForm); setShowAddModal(true); }}>
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add</span>
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-slate-500">Total Trans.</p>
                <p className="text-lg sm:text-2xl font-bold">{summary.count}</p>
              </div>
              <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-slate-500">Amount Due</p>
                <p className="text-lg sm:text-2xl font-bold truncate">{formatCurrency(summary.amount_due)}</p>
              </div>
              <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-slate-500">Agent Comm.</p>
                <p className="text-lg sm:text-2xl font-bold truncate">{formatCurrency(summary.agent_comm)}</p>
              </div>
              <User className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-slate-500">Net to Brkge</p>
                <p className="text-lg sm:text-2xl font-bold truncate">{formatCurrency(summary.net_to_brokerage)}</p>
              </div>
              <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* By Transaction Type Summary */}
      <div className="flex gap-2 sm:gap-4 flex-wrap">
        {Object.entries(summary.by_type).map(([type, data]) => (
          <Badge key={type} variant="outline" className={`px-2 sm:px-3 py-1 text-xs sm:text-sm ${getTypeBadge(type)}`}>
            {type}: {data.count} ({formatCurrency(data.amount_due)})
          </Badge>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 sm:gap-4 items-center">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500 hidden sm:block" />
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-20 sm:w-24 touch-manipulation">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all_">All</SelectItem>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {!isAgent && (
          <Select value={filterAgent} onValueChange={setFilterAgent}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Agents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all_">All Agents</SelectItem>
              {agents.filter(a => a.id).map(a => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={filterCarrier} onValueChange={setFilterCarrier}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Carriers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all_">All Carriers</SelectItem>
            {carriers.filter(c => c).map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterTransType} onValueChange={setFilterTransType}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all_">All Types</SelectItem>
            <SelectItem value="FYO">FYO</SelectItem>
            <SelectItem value="FYC">FYC</SelectItem>
            <SelectItem value="RENEW">RENEW</SelectItem>
            <SelectItem value="TRANSFER">TRANSFER</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search all data..."
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Bulk Delete Button */}
        {!isAgent && selectedIds.size > 0 && (
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => setBulkDeleteConfirm(true)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Selected ({selectedIds.size})
          </Button>
        )}
      </div>

      {/* Expand/Collapse Controls */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Button variant="outline" size="sm" onClick={expandAllYears}>
          <ChevronDown className="w-4 h-4 mr-1" />
          Expand All
        </Button>
        <Button variant="outline" size="sm" onClick={collapseAllYears}>
          <ChevronRight className="w-4 h-4 mr-1" />
          Collapse All
        </Button>
        
        {/* New items indicator and mark all viewed button */}
        {filteredCommissions.filter(c => c.is_new).length > 0 && !isAgent && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={markAllAsViewed}
            className="ml-2 border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700"
          >
            <Eye className="w-4 h-4 mr-1" />
            Mark All Viewed ({filteredCommissions.filter(c => c.is_new).length} new)
          </Button>
        )}
        
        <span className="text-sm text-slate-500 ml-2">
          {groupedByYear.length} year(s), {filteredCommissions.length} total transactions
        </span>
      </div>

      {/* Commissions Grouped by Year */}
      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">Loading...</CardContent>
        </Card>
      ) : groupedByYear.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            No commissions found for the selected filters
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groupedByYear.map(([year, yearData]) => (
            <Card key={year} className="border-slate-200">
              <Collapsible open={expandedYears[year]} onOpenChange={() => toggleYear(year)}>
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      {expandedYears[year] ? (
                        <ChevronDown className="w-5 h-5 text-slate-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-blue-500" />
                        <span className="font-bold text-lg text-slate-900">Year {year}</span>
                        <Badge variant="secondary" className="ml-2">
                          {yearData.totals.count} transactions
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-right">
                        <span className="text-slate-500">Amount Due:</span>
                        <span className="font-semibold ml-2">{formatCurrency(yearData.totals.amount_due)}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-slate-500">Agent Comm:</span>
                        <span className="font-semibold text-green-600 ml-2">{formatCurrency(yearData.totals.agent_comm)}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-slate-500">Net to Brokerage:</span>
                        <span className="font-semibold text-blue-600 ml-2">{formatCurrency(yearData.totals.net_to_brokerage)}</span>
                      </div>
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="border-t border-slate-200">
                    <div 
                      className="overflow-auto -webkit-overflow-scrolling-touch scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100"
                      style={{ maxHeight: '70vh' }}
                      onWheel={(e) => {
                        // Enable horizontal scroll with mouse wheel
                        if (e.deltaY !== 0) {
                          e.currentTarget.scrollLeft += e.deltaY;
                          e.preventDefault();
                        }
                      }}
                    >
                      <Table className="min-w-[1200px]">
                        <TableHeader className="sticky top-0 z-30">
                          <TableRow>
                            {!isAgent && (
                              <TableHead className="w-10 sm:w-12 sticky left-0 z-40 bg-slate-50" style={{ backgroundColor: '#f8fafc' }}>
                                <Checkbox
                                  checked={yearData.commissions.length > 0 && yearData.commissions.every(c => selectedIds.has(c.id))}
                                  onCheckedChange={() => {
                                    const yearIds = yearData.commissions.map(c => c.id);
                                    const allSelected = yearIds.every(id => selectedIds.has(id));
                                    if (allSelected) {
                                      setSelectedIds(prev => {
                                        const next = new Set(prev);
                                        yearIds.forEach(id => next.delete(id));
                                        return next;
                                      });
                                    } else {
                                      setSelectedIds(prev => new Set([...prev, ...yearIds]));
                                    }
                                  }}
                                />
                              </TableHead>
                            )}
                            <TableHead className={`whitespace-nowrap sticky ${isAgent ? 'left-0' : 'left-[48px]'} z-40 min-w-[80px] border-r border-slate-200`} style={{ backgroundColor: '#f8fafc' }}>Carrier</TableHead>
                            <TableHead className={`whitespace-nowrap sticky ${isAgent ? 'left-[80px]' : 'left-[128px]'} z-40 min-w-[120px] border-r border-slate-200`} style={{ backgroundColor: '#f8fafc' }}>Agent</TableHead>
                            <TableHead className={`whitespace-nowrap sticky ${isAgent ? 'left-[200px]' : 'left-[248px]'} z-40 min-w-[90px] border-r border-slate-200`} style={{ backgroundColor: '#f8fafc' }}>Category</TableHead>
                            <TableHead className={`whitespace-nowrap sticky ${isAgent ? 'left-[290px]' : 'left-[338px]'} z-40 min-w-[120px] border-r border-slate-200`} style={{ backgroundColor: '#f8fafc' }}>Insured</TableHead>
                            <TableHead className={`whitespace-nowrap sticky ${isAgent ? 'left-[410px]' : 'left-[458px]'} z-40 min-w-[100px] border-r border-slate-200`} style={{ backgroundColor: '#f8fafc' }}>Contract No</TableHead>
                            <TableHead className={`whitespace-nowrap sticky ${isAgent ? 'left-[510px]' : 'left-[558px]'} z-40 min-w-[100px] border-r border-slate-200`} style={{ backgroundColor: '#f8fafc' }}>Coverage</TableHead>
                            <TableHead className={`whitespace-nowrap sticky ${isAgent ? 'left-[610px]' : 'left-[658px]'} z-40 min-w-[90px] border-r border-slate-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ backgroundColor: '#f8fafc' }}>Date</TableHead>
                            <TableHead className="whitespace-nowrap" style={{ backgroundColor: '#f8fafc' }}>Type</TableHead>
                            <TableHead className="text-right whitespace-nowrap" style={{ backgroundColor: '#f8fafc' }}>Basis Amt</TableHead>
                            <TableHead className="text-right whitespace-nowrap" style={{ backgroundColor: '#f8fafc' }}>Comm %</TableHead>
                            <TableHead className="text-right whitespace-nowrap" style={{ backgroundColor: '#f8fafc' }}>Amount Due</TableHead>
                            <TableHead className="text-right whitespace-nowrap" style={{ backgroundColor: '#f8fafc' }}>Agent Split</TableHead>
                            <TableHead className="text-right whitespace-nowrap" style={{ backgroundColor: '#f8fafc' }}>Agent Comm</TableHead>
                            <TableHead className="whitespace-nowrap" style={{ backgroundColor: '#f8fafc' }}>Paid to Agent</TableHead>
                            <TableHead className="text-right whitespace-nowrap" style={{ backgroundColor: '#f8fafc' }}>Net to Brkge</TableHead>
                            {!isAgent && <TableHead className="w-20" style={{ backgroundColor: '#f8fafc' }}>Actions</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {yearData.commissions.map(commission => (
                            <TableRow 
                              key={commission.id}
                              data-commission-id={commission.id}
                              className={`
                                ${selectedIds.has(commission.id) ? 'bg-blue-50' : ''} 
                                ${commission.is_new ? 'bg-amber-50 font-semibold border-l-4 border-l-amber-500' : ''} 
                                ${highlightedCommissionId === commission.id ? 'bg-green-100 border-l-4 border-l-green-500 animate-pulse' : ''}
                                cursor-pointer hover:bg-slate-50 transition-colors
                              `}
                              onClick={() => {
                                if (commission.is_new) {
                                  markAsViewed(commission.id);
                                }
                              }}
                            >
                              {!isAgent && (
                                <TableCell className="sticky left-0 bg-white z-20" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={selectedIds.has(commission.id)}
                                    onCheckedChange={() => toggleSelection(commission.id)}
                                  />
                                </TableCell>
                              )}
                              <TableCell className={`font-medium sticky ${isAgent ? 'left-0' : 'left-[48px]'} bg-white z-10 min-w-[80px] border-r border-slate-100`} onClick={() => handleEdit(commission)}>{commission.carrier}</TableCell>
                              <TableCell className={`sticky ${isAgent ? 'left-[80px]' : 'left-[128px]'} bg-white z-10 min-w-[120px] border-r border-slate-100`} onClick={() => handleEdit(commission)}>
                                {commission.is_brokerage_direct ? (
                                  <span className="inline-flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                    <span className="text-amber-700 text-sm">Brokerage Direct</span>
                                  </span>
                                ) : commission.agent_name}
                              </TableCell>
                              <TableCell className={`sticky ${isAgent ? 'left-[200px]' : 'left-[248px]'} bg-white z-10 min-w-[90px] border-r border-slate-100`} onClick={() => handleEdit(commission)}>
                                <Badge variant="outline" className="text-xs">
                                  {commission.category || 'OTHER'}
                                </Badge>
                              </TableCell>
                              <TableCell className={`sticky ${isAgent ? 'left-[290px]' : 'left-[338px]'} bg-white z-10 min-w-[120px] border-r border-slate-100`} onClick={() => handleEdit(commission)}>{commission.insured_name}</TableCell>
                              <TableCell className={`font-mono text-sm sticky ${isAgent ? 'left-[410px]' : 'left-[458px]'} bg-white z-10 min-w-[100px] border-r border-slate-100`} onClick={() => handleEdit(commission)}>{commission.contract_no}</TableCell>
                              <TableCell className={`sticky ${isAgent ? 'left-[510px]' : 'left-[558px]'} bg-white z-10 min-w-[100px] border-r border-slate-100`} onClick={() => handleEdit(commission)}>{commission.coverage_name}</TableCell>
                              <TableCell className={`sticky ${isAgent ? 'left-[610px]' : 'left-[658px]'} bg-white z-10 min-w-[90px] border-r border-slate-100 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} onClick={() => handleEdit(commission)}>{formatDate(commission.transaction_date)}</TableCell>
                              <TableCell onClick={() => handleEdit(commission)}>
                                <Badge className={getTypeBadge(commission.transaction_type)}>
                                  {commission.transaction_type}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right" onClick={() => handleEdit(commission)}>{formatCurrency(commission.basis_amount)}</TableCell>
                              <TableCell className="text-right" onClick={() => handleEdit(commission)}>{commission.comm_pct}%</TableCell>
                              <TableCell className="text-right font-medium" onClick={() => handleEdit(commission)}>{formatCurrency(commission.amount_due)}</TableCell>
                              <TableCell className="text-right" onClick={() => handleEdit(commission)}>{commission.agent_split_pct}%</TableCell>
                              <TableCell className="text-right text-green-600" onClick={() => handleEdit(commission)}>{formatCurrency(commission.agent_comm)}</TableCell>
                              <TableCell onClick={() => handleEdit(commission)}>{formatDate(commission.paid_to_agent_date)}</TableCell>
                              <TableCell className="text-right text-blue-600" onClick={() => handleEdit(commission)}>{formatCurrency(commission.net_comm_to_brokerage)}</TableCell>
                              {!isAgent && (
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  <div className="flex gap-1">
                                    <Button variant="ghost" size="sm" onClick={() => handleEdit(commission)}>
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(commission)}>
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Commission Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCommission ? 'Edit Commission' : 'Add Commission'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Brokerage Direct Toggle */}
            <div className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50" data-testid="mga-brokerage-direct-toggle">
              <Checkbox
                id="mga_is_brokerage_direct"
                checked={formData.is_brokerage_direct}
                onCheckedChange={(checked) => {
                  setFormData({
                    ...formData,
                    is_brokerage_direct: checked,
                    ...(checked ? { agent_id: '', agent_split_pct: '0' } : { agent_split_pct: '' })
                  });
                }}
                data-testid="mga-brokerage-direct-checkbox"
              />
              <div>
                <Label htmlFor="mga_is_brokerage_direct" className="font-semibold text-amber-800 cursor-pointer">
                  Direct Brokerage Income (No Agent)
                </Label>
                <p className="text-xs text-amber-600 mt-0.5">
                  Check this if income comes directly to the brokerage. 100% of commission stays with the brokerage.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Carrier</Label>
                  {!isAgent && (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs text-blue-600"
                      onClick={() => setShowCarrierManager(true)}
                    >
                      Manage Carriers
                    </Button>
                  )}
                </div>
                <CarrierCombobox
                  carriers={carriers}
                  value={formData.carrier}
                  onChange={(v) => setFormData({ ...formData, carrier: v })}
                />
              </div>
              {!formData.is_brokerage_direct ? (
                <>
                  <div className="space-y-2">
                    <Label>Agent</Label>
                    <Select value={formData.agent_id} onValueChange={(v) => setFormData({ ...formData, agent_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select agent" />
                      </SelectTrigger>
                      <SelectContent>
                        {agents.filter(a => a.id).map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Agent Code</Label>
                    <Input
                      value={formData.agent_code}
                      onChange={(e) => setFormData({ ...formData, agent_code: e.target.value })}
                    />
                  </div>
                </>
              ) : (
                <div className="col-span-2 p-3 bg-slate-50 rounded-lg border border-slate-200 text-center">
                  <p className="text-sm text-slate-600 font-medium">Agent fields hidden — income recorded directly to brokerage</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INSURANCE">Insurance</SelectItem>
                    <SelectItem value="INVESTMENT">Investment</SelectItem>
                    <SelectItem value="TRAVEL">Travel</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Insured Name</Label>
                <Input
                  value={formData.insured_name}
                  onChange={(e) => setFormData({ ...formData, insured_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Contract No</Label>
                <Input
                  value={formData.contract_no}
                  onChange={(e) => setFormData({ ...formData, contract_no: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Coverage Name</Label>
                <Input
                  value={formData.coverage_name}
                  onChange={(e) => setFormData({ ...formData, coverage_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Transaction Date</Label>
                <Input
                  type="date"
                  value={formData.transaction_date}
                  onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.transaction_type} onValueChange={(v) => setFormData({ ...formData, transaction_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FYO">FYO - First Year Override</SelectItem>
                    <SelectItem value="FYC">FYC - First Year Commission</SelectItem>
                    <SelectItem value="RENEW">RENEW - Renewal</SelectItem>
                    <SelectItem value="TRANSFER">TRANSFER - Transfer from Affiliated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Basis Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.basis_amount}
                  onChange={(e) => setFormData({ ...formData, basis_amount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>RPL %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.rpl_pct}
                  onChange={(e) => setFormData({ ...formData, rpl_pct: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Share %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.share_pct}
                  onChange={(e) => setFormData({ ...formData, share_pct: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Comm %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.comm_pct}
                  onChange={(e) => setFormData({ ...formData, comm_pct: e.target.value })}
                />
              </div>
              {!formData.is_brokerage_direct && (
                <>
                  <div className="space-y-2">
                    <Label>Agent Split %</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.agent_split_pct}
                      onChange={(e) => setFormData({ ...formData, agent_split_pct: e.target.value })}
                      placeholder="Auto from agent settings"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Paid to Agent Date</Label>
                    <Input
                      type="date"
                      value={formData.paid_to_agent_date}
                      onChange={(e) => setFormData({ ...formData, paid_to_agent_date: e.target.value })}
                    />
                  </div>
                </>
              )}
              <div className="space-y-2 col-span-2">
                <Label>Chart of Account <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.accounting_account_id || ""}
                  onValueChange={(v) => setFormData({ ...formData, accounting_account_id: v })}
                >
                  <SelectTrigger data-testid="mga-commission-account-select">
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {chartOfAccounts.filter(a => a.is_active !== false).map((acc) => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.account_number} - {acc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Memo</Label>
                <Input
                  value={formData.memo}
                  onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
                />
              </div>
            </div>

            {/* Calculated Preview */}
            {formData.basis_amount && formData.comm_pct && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Amount Due:</strong> {formatCurrency((parseFloat(formData.basis_amount) || 0) * (parseFloat(formData.comm_pct) || 0) / 100)}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Agent commission will be calculated based on agent's split for {formData.transaction_type}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button type="submit">{editingCommission ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Commissions</DialogTitle>
          </DialogHeader>
          
          {importData && (
            <div className="space-y-4">
              <div className="flex justify-between text-sm text-slate-600">
                <span>
                  File type: <Badge variant="outline" className="ml-1">{importData.file_type?.toUpperCase() || 'EXCEL'}</Badge>
                </span>
                <span>Total rows: {importData.total_rows}</span>
                <span>Agents found: {importData.agents_found?.join(', ') || 'None'}</span>
              </div>

              {importData.file_type === 'pdf' && (
                <div className="p-3 bg-blue-50 text-blue-700 rounded text-sm">
                  PDF file converted to tabular format for mapping. Please verify column mappings below.
                </div>
              )}

              {/* Column Mappings */}
              <div className="space-y-2">
                <h4 className="font-medium">Column Mappings</h4>
                <div className="grid grid-cols-2 gap-4">
                  {importData.system_fields?.map(field => (
                    <div key={field.field} className="flex items-center gap-2">
                      <Label className="w-32 text-sm">{field.label}{field.required && ' *'}</Label>
                      <Select
                        value={importMappings[field.field] || '_skip_'}
                        onValueChange={(v) => setImportMappings({ ...importMappings, [field.field]: v === '_skip_' ? '' : v })}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_skip_">-- Skip --</SelectItem>
                          {importData.file_columns?.filter(col => col).map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview Data */}
              <div className="space-y-2">
                <h4 className="font-medium">Preview Data</h4>
                <div className="overflow-x-auto max-h-48 border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {importData.file_columns?.slice(0, 8).map(col => (
                          <TableHead key={col} className="text-xs">{col}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importData.preview_data?.slice(0, 5).map((row, i) => (
                        <TableRow key={i}>
                          {importData.file_columns?.slice(0, 8).map(col => (
                            <TableCell key={col} className="text-xs">{row[col]}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
            <Button onClick={handleImportExecute} disabled={importLoading}>
              {importLoading ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Commission?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this commission for {deleteConfirm?.insured_name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Commission(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.size} selected commission(s)? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700">
              Delete All Selected
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Carrier Manager Dialog */}
      <Dialog open={showCarrierManager} onOpenChange={setShowCarrierManager}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Carriers</DialogTitle>
            <DialogDescription>
              Remove carriers from the dropdown list. This won't delete any commissions.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {carriers.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No carriers found</p>
            ) : (
              <div className="space-y-1">
                {carriers.map(carrier => (
                  <div key={carrier} className="flex items-center justify-between p-2 rounded hover:bg-slate-50">
                    <span className="text-sm">{carrier}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => handleDeleteCarrier(carrier)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCarrierManager(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
